import { Card, Grid, Icon, ToggleButton, ToggleButtonGroup } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getRoute } from "utils/routing";
import OpportunitiesList from "../../modules/companies/CompaniesList/OpportunitiesList";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";
import { UserPermissions, UserRole } from "generated/graphql";
import { useEffect, useRef, useState } from "react";
import useGetOpportunityStatuses from "hooks/opportunities/useGetOpportunityStatuses";
import OpportunityKanban from "modules/Opportunities/OpportunityKanban";
import ManagePipelineOrderModal from "modules/Opportunities/ManagePipelineOrderModal";
import { useBitumioContext } from "providers/BitumioContext";
import PageTour, { PageTourHandle } from "components/PageTour/PageTour";
import TourHelpButton from "components/PageTour/TourHelpButton";
import opportunitiesPageTourSteps from "./OpportunitiesPage.tour";

export default function OpportunitiesPage() {
  const { hasPermissions } = useCheckPermissions();
  const tourRef = useRef<PageTourHandle>(null);
  const [tourSteps, setTourSteps] = useState(opportunitiesPageTourSteps);
  const [tourActive, setTourActive] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [{ signedInUser }] = useBitumioContext();
  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");
  const [managePipelineOpen, setManagePipelineOpen] = useState(false);
  const { data: opportunityStatuses } = useGetOpportunityStatuses();
  const isAdmin = signedInUser?.roles?.includes(UserRole.ADMIN) ?? false;

  useEffect(() => {
    const savedView = localStorage.getItem("opportunitiesViewMode");
    if (savedView === "kanban" || savedView === "table") {
      setViewMode(savedView);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("opportunitiesViewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    if ((location.state as { startTour?: boolean })?.startTour) {
      setTourSteps(opportunitiesPageTourSteps.slice(1));
      setTourActive(true);
      tourRef.current?.replay();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <DashboardPage>
      <PageTour
        ref={tourRef}
        tourKey="opportunitiesPage"
        steps={tourSteps}
        onFinish={() => setTourActive(false)}
      />
      <MDBox mb={6}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <Card>
              <MDBox
                p={3}
                lineHeight={1}
                display="flex"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <MDTypography variant="h4" fontWeight="medium">
                  Opportunities
                  <TourHelpButton
                    tooltip="Click here to learn more about Creating Opportunities"
                    onClick={() => {
                      setTourSteps(opportunitiesPageTourSteps);
                      setTourActive(true);
                      tourRef.current?.replay();
                    }}
                  />
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  {hasPermissions(UserPermissions.MANAGE_OPPORTUNITIES) && isAdmin && (
                    <MDButton
                      className="tour-manage-pipeline-order"
                      color="secondary"
                      onClick={() => {
                        setManagePipelineOpen(true);
                        document
                          .querySelector<HTMLButtonElement>('[data-testid="button-primary"]')
                          ?.click();
                      }}
                    >
                      Manage Pipeline Order
                    </MDButton>
                  )}
                  {hasPermissions(UserPermissions.MANAGE_OPPORTUNITIES) && (
                    <MDTypography
                      as={Link}
                      to={getRoute("opportunities.create")}
                      state={tourActive ? { startTour: true } : undefined}
                    >
                      <MDButton className="tour-create-opportunity" color="blue">
                        <Icon>add</Icon>&nbsp; Create Opportunity
                      </MDButton>
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {viewMode === "table" ? (
            <OpportunitiesList
              headerRight={
                <ToggleButtonGroup
                  className="tour-view-toggle"
                  value={viewMode}
                  exclusive
                  size="small"
                  onChange={(_, nextValue) => {
                    if (nextValue) {
                      setViewMode(nextValue);
                    }
                  }}
                >
                  <ToggleButton value="table" aria-label="Table view">
                    <Icon fontSize="small">view_list</Icon>
                  </ToggleButton>
                  <ToggleButton value="kanban" aria-label="Kanban view">
                    <Icon fontSize="small">view_column</Icon>
                  </ToggleButton>
                </ToggleButtonGroup>
              }
            />
          ) : (
            <OpportunityKanban
              statuses={opportunityStatuses}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          )}
        </Grid>
      </Grid>
      <ManagePipelineOrderModal
        open={managePipelineOpen}
        onClose={() => setManagePipelineOpen(false)}
        statuses={opportunityStatuses}
      />
    </DashboardPage>
  );
}
